import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Field, Form, Formik } from 'formik';
import { toast } from 'react-toastify';

// ours
import { CircledCheckmarkIcon } from '../../atoms/Icons';
import LabelledColumn from '../../atoms/LabelledColumn';
import Button from '../../atoms/Button';
import TextInput from '../../atoms/TextInput/TextInput';
import { SelectSelfLoading } from '../../atoms/Select';
import { UPSERT_ORG_NOTIFICATION_PLAN_GQL } from '../OrgManagementPage/queries';
import './Workshop.scss';

const SuccessUpdated = () =>
  toast.success('Successfully Updated', {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

const ErrorOccurred = () =>
  toast.warn('Error Occurred', {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

/**
 * ! THIS IS ALL C&P'd from SingleFieldForm
 * Less work than rewriting it all and maybe not appropriate for that component since I _think_ most tables have multiple required fields
 * Org and barn just happen to be 1
 */
export default function WorkshopBarnOrOrgCreationForm({
  fieldLabel, // If you want to display something different than the hasura name
  placeholder, // Overrides fieldLabel for the inline placeholder if needed
  fieldHasuraName, // name of the column in hasura
  fieldTableName, // name of the table in hasura
  rowId, // since you want to update you MUST provide a primary key, which MUST be the 'id' column (for now)
  onSuccess = () => {},
  onError = () => {},
  extraVariables = {},
  // basic/expected stuff
  className = null,
  inputWrapperClassName = '',
  textInputClassName = '',
  buttonClassName = '',
  style = {},
}) {
  // --- State hooks ---
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // --- Navigation/side effects ---
  useEffect(() => {
    let timerId = null;

    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
        // switch back to button after 3.5s
      }, 3500);
    }

    // cleanup timer
    return () => clearTimeout(timerId);
  }, [showSuccess]);

  // --- Derived state ---
  // What are we showing people?
  const displayName = fieldLabel || fieldHasuraName;
  const isOrgCreation = fieldTableName === 'organization';

  // Set up a dynamic query
  const fieldQuery = gql`query WorkshopBarnOrOrgCreationForm_${fieldHasuraName}($rowId: uuid!) {
	${fieldTableName}_by_pk(id: $rowId) {
		${fieldHasuraName}
	}
  }`;

  // set up a dynamic mutation
  // * Manually added ID To this v singlefieldform
  const fieldMutation = gql`
    mutation WorkshopBarnOrOrgCreationForm_Insert${fieldTableName}_${fieldHasuraName}($newRowObject: ${fieldTableName}_insert_input!) {
      insert_${fieldTableName}_one(object: $newRowObject) {
        id
        ${fieldHasuraName}
      }
    }
  `;

  // --- Apollo/GraphQL hooks ---
  // get us the initial values to display
  const { data, loading } = useQuery(fieldQuery, {
    variables: {
      rowId,
    },
    skip: !rowId,
  });

  // generate the save function
  const [saveField, { loading: savingField }] = useMutation(fieldMutation);

  // mutation for saving notification plan (only used for org creation)
  const [upsertOrgNotificationPlan] = useMutation(UPSERT_ORG_NOTIFICATION_PLAN_GQL, {
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  // initial values extracted
  const initFieldValue = data?.[`${fieldTableName}_by_pk`]?.[fieldHasuraName];

  // simple loader
  if (loading) return <div>Loading...</div>;

  return (
    <Formik
      initialValues={{
        [fieldHasuraName]: initFieldValue || '',
      }}
      onSubmit={async (values, formikBag) => {
        // Validate notification plan is selected for org creation
        if (isOrgCreation && !selectedPlan) {
          toast('Please select a notification plan', { type: 'warning' });
          return;
        }

        // save by sending the mutation then fire callbacks
        await saveField({
          variables: {
            newRowObject: {
              [fieldHasuraName]: values[fieldHasuraName],
              ...extraVariables,
            },
          },
          onCompleted: async (data) => {
            if (!data?.[`insert_${fieldTableName}_one`]) {
              // if nothing is returned for this request it must have errored
              onError(data); // dev provided callback
              ErrorOccurred(); // toast
            } else {
              const createdOrgId = data?.[`insert_${fieldTableName}_one`]?.id;

              // If creating an org, also save the notification plan
              if (isOrgCreation && selectedPlan && createdOrgId) {
                try {
                  await upsertOrgNotificationPlan({
                    variables: {
                      org_id: createdOrgId,
                      notification_tier_plan_id: selectedPlan,
                    },
                  });
                } catch (error) {
                  console.error('Error saving notification plan:', error);
                  toast(`Organization created but failed to save notification plan: ${error.message}`, {
                    type: 'error',
                  });
                  onError(error);
                  ErrorOccurred();
                  return;
                }
              }

              // Setting the value for the field in case they differ.
              formikBag.setValues({ [fieldHasuraName]: data?.[`insert_${fieldTableName}_one`]?.[fieldHasuraName] });

              // First arg is probably what you want as a dev, but providing whole object just in case
              onSuccess(data?.[`insert_${fieldTableName}_one`]?.[fieldHasuraName], data); // dev provided callback
              setShowSuccess(true);
              SuccessUpdated(); // toast
            }
          },
          onError: (error) => {
            console.log('WHAT HAPPENED');
            console.error(error);
            onError(error); // dev provided callback
            // TODO: (future, minor). We could display an error below the field but we'll see if that's more useful than the toast
            ErrorOccurred(); // toast
          },
        });
      }}
    >
      {({ isSubmitting, errors }) => (
        <Form className={[`WorkshopBarnOrOrgCreationForm`, className].join(' ')} style={style}>
          <LabelledColumn
            heading={displayName}
            loading={loading}
            className={`WorkshopBarnOrOrgCreationForm-labelledColumn ${inputWrapperClassName}`}
          >
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Field
                as={TextInput}
                errors={errors?.[fieldHasuraName]}
                name={fieldHasuraName}
                placeholder={placeholder || displayName}
                disabled={isSubmitting || savingField}
                inputClassName={`WorkshopBarnOrOrgCreationForm-textInput ${textInputClassName}`}
                right={
                  <Button
                    disabled={isSubmitting || showSuccess || (isOrgCreation && !selectedPlan)}
                    type="submit"
                    variant="vivid"
                    color="success"
                    content={
                      showSuccess ? (
                        <CircledCheckmarkIcon className="WorkshopBarnOrOrgCreationForm-checkmark" />
                      ) : (
                        'CREATE'
                      )
                    }
                    className={`WorkshopBarnOrOrgCreationForm-saveButton ${buttonClassName}`}
                  />
                }
              />
            </div>
          </LabelledColumn>
          {isOrgCreation && (
            <LabelledColumn
              heading="Notification Plan"
              loading={false}
              className={`WorkshopBarnOrOrgCreationForm-labelledColumn ${inputWrapperClassName}`}
            >
              <SelectSelfLoading
                label="Select Notification Plan"
                defaultText="Select a Plan"
                defaultTextDisabled={false}
                fieldTableName="notification_plan"
                onChange={(x) => {
                  setSelectedPlan(x.target.value);
                }}
                value={selectedPlan || ''}
                disabled={isSubmitting || savingField}
              />
            </LabelledColumn>
          )}
        </Form>
      )}
    </Formik>
  );
}

WorkshopBarnOrOrgCreationForm.propTypes = {
  extraVariables: PropTypes.object, // added for barn creation
  fieldLabel: PropTypes.string,
  fieldHasuraName: PropTypes.string.isRequired,
  fieldTableName: PropTypes.string.isRequired,
  rowId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
  inputWrapperClassName: PropTypes.string,
  textInputClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  style: PropTypes.object,
  placeholder: PropTypes.string,
};
